// SPDX-License-Identifier: Elastic-2.0
/**
 * DeviceModal — detailed device-control modal (T-024).
 *
 * Per functional spec § 5 + task spec:
 *   - Header: device name + room badge + close X (from Modal primitive).
 *   - State line: large readout (e.g. "ON · 80%") derived per-type.
 *   - Per-type controls: dispatched to one of LightControls /
 *     ClimateControls / MediaControls / LockControls / SwitchControls /
 *     SensorView / GenericView based on `device.type`.
 *   - History snippet (last 5) — STUB at v0.1 since F-300 has no
 *     `/devices/{id}/history` endpoint yet.
 *   - "Add to routine" CTA → `navigate("/routines/new?prefill=device:{id}")`.
 *     T-026 wires the actual prefill behaviour; at v0.1 we just navigate.
 *
 * Failure handling: each control dispatches via
 * `useControlDevice.mutate(...)`. On error we surface a Banner inline at
 * the top of the body and rely on the mutation hook to revert the
 * optimistic cache patch. The Banner auto-clears after the next
 * successful mutation.
 *
 * Loading: when the modal opens we lean on `useDevice(id)` for the full
 * detail payload (gives us `diagnostics`). The data we already have from
 * the list query is the immediate placeholder so the modal never flashes
 * a spinner if the cached object is recent.
 */
import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { Banner } from "@/components/Banner/Banner";
import { Button } from "@/components/Button";
import { Icon } from "@/components/Icon/Icon";
import { Modal } from "@/components/Modal";

import { DEVICES_QUERY_KEY, useControlDevice, useDevice } from "../dashboard/useDevices";

import type { DeviceListResponse } from "@/api/gateway";

import { HistorySnippet } from "./HistorySnippet";
import { ClimateControls } from "./controls/ClimateControls";
import { GenericView } from "./controls/GenericView";
import { LightControls } from "./controls/LightControls";
import { LockControls } from "./controls/LockControls";
import { MediaControls } from "./controls/MediaControls";
import { SensorView } from "./controls/SensorView";
import { SwitchControls } from "./controls/SwitchControls";

import styles from "./DeviceModal.module.css";

import type { Device } from "@/api/gateway";

export interface DeviceModalProps {
  /**
   * Device to render. Pass `null` (or omit) to keep the modal closed.
   * Either an already-loaded `Device` (the dashboard hands us the cached
   * list entry) or a bare deviceId — the modal looks the cache up when
   * given a bare id.
   */
  device?: Device | null;
  deviceId?: string | null;
  /** Called when the user dismisses (Esc, backdrop, X, after add-to-routine). */
  onClose: () => void;
}

/** Render the secondary state line (per task spec § 1 + functional § 5). */
function formatStateLine(device: Device, t: (k: string) => string): string {
  const state = device.state ?? {};
  switch (device.type) {
    case "light": {
      const on = Boolean(state.on);
      if (!on) return t("dashboard.devices.off");
      const brightness = typeof state.brightness === "number" ? state.brightness : undefined;
      return brightness !== undefined
        ? `${t("dashboard.devices.on")} · ${Math.round(brightness)}%`
        : t("dashboard.devices.on");
    }
    case "switch": {
      return state.on
        ? t("dashboard.devices.on")
        : t("dashboard.devices.off");
    }
    case "sensor": {
      const value = state.value ?? state.state;
      const unit = typeof state.unit === "string" ? state.unit : "";
      return value === undefined || value === null
        ? "—"
        : `${value}${unit ? ` ${unit}` : ""}`;
    }
    case "climate": {
      const temp = state.current_temperature ?? state.temperature;
      const unit = typeof state.unit === "string" ? state.unit : "°C";
      return typeof temp === "number" ? `${temp}${unit}` : "—";
    }
    case "media_player": {
      const playing =
        typeof state.playback === "string"
          ? state.playback.toLowerCase() === "playing"
          : Boolean(state.playback);
      return playing
        ? t("dashboard.devices.playing")
        : t("dashboard.devices.paused");
    }
    case "lock": {
      return state.locked || state.is_locked
        ? t("dashboard.devices.locked")
        : t("dashboard.devices.unlocked");
    }
    default:
      return "";
  }
}

/** Pick the correct controls component for the device type. */
function renderControls(
  device: Device,
  dispatch: ReturnType<typeof makeDispatch>,
  disabled: boolean,
): ReactNode {
  switch (device.type) {
    case "light":
      return <LightControls device={device} onAction={dispatch} disabled={disabled} />;
    case "switch":
      return <SwitchControls device={device} onAction={dispatch} disabled={disabled} />;
    case "climate":
      return <ClimateControls device={device} onAction={dispatch} disabled={disabled} />;
    case "media_player":
      return <MediaControls device={device} onAction={dispatch} disabled={disabled} />;
    case "lock":
      return <LockControls device={device} onAction={dispatch} disabled={disabled} />;
    case "sensor":
      return <SensorView device={device} />;
    case "cover":
    case "generic":
    default:
      return <GenericView device={device} />;
  }
}

/**
 * Build the per-action dispatcher passed down to control components.
 * Captured outside the component body so the type of the second
 * argument to `renderControls` is stable.
 */
function makeDispatch(
  device: Device,
  mutate: ReturnType<typeof useControlDevice>["mutate"],
  setError: (message: string | null) => void,
): (
  action: string,
  params: Record<string, unknown>,
  optimisticStatePatch?: Record<string, unknown>,
) => void {
  return (action, params, optimisticStatePatch) => {
    setError(null);
    mutate(
      {
        deviceId: device.id,
        request: { action, params: params ?? {} },
        optimisticStatePatch,
      },
      {
        onError: (err) => {
          setError(err.message || "control_failed");
        },
      },
    );
  };
}

export function DeviceModal({
  device,
  deviceId,
  onClose,
}: DeviceModalProps): JSX.Element | null {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  // Resolve a Device from the inputs: explicit `device` prop wins, else
  // look the id up in the list cache that `useDevices()` populates.
  const resolvedId = device?.id ?? deviceId ?? undefined;
  const fromListCache =
    resolvedId !== undefined && device === undefined
      ? queryClient
          .getQueryData<DeviceListResponse>(DEVICES_QUERY_KEY)
          ?.devices.find((d) => d.id === resolvedId)
      : undefined;

  // Refetch detail (incl. diagnostics) when open. The query is cheap
  // and shares a cache key with the list, so re-renders are free.
  const detailQuery = useDevice(resolvedId);
  const controlMutation = useControlDevice();

  const seed = device ?? fromListCache ?? detailQuery.data ?? null;
  if (!seed) return null;

  // Prefer the freshly-fetched detail (has `diagnostics`); fall back to
  // the list-cache / explicit-prop device.
  const current: Device = detailQuery.data ?? seed;
  const disabled = current.available === false;
  const stateLine = formatStateLine(current, t);
  const dispatch = makeDispatch(current, controlMutation.mutate, setError);

  function handleAddToRoutine(): void {
    navigate(`/routines/new?prefill=device:${current.id}`);
    onClose();
  }

  const title = current.name;

  return (
    <Modal
      open={true}
      onClose={onClose}
      title={title}
      size="lg"
      footer={
        <Button
          variant="secondary"
          onClick={handleAddToRoutine}
          data-testid="add-to-routine"
        >
          <Icon name="workflow" size={16} />
          {" "}
          {t("device_modal.add_to_routine")}
        </Button>
      }
    >
      <div className={styles.body} data-testid="device-modal-body">
        {current.room ? (
          <div className={styles.roomBadge} data-testid="device-modal-room">
            {current.room}
          </div>
        ) : null}
        {stateLine ? (
          <div className={styles.stateLine} data-testid="device-modal-state-line">
            {stateLine}
          </div>
        ) : null}
        {error ? (
          <Banner
            kind="error"
            message={t("dashboard.devices.control_failed")}
            dismissable
            onDismiss={() => setError(null)}
          />
        ) : null}
        <div className={styles.controlsBlock}>{renderControls(current, dispatch, disabled)}</div>
        <HistorySnippet deviceId={current.id} limit={5} />
      </div>
    </Modal>
  );
}

export default DeviceModal;
